export interface LOProfile {
  loan_officer_id: string;
  web_app_id: string;
  name: string;
  first_name?: string;
  last_name?: string;
  headshot_url: string | null;
  license: string | null;
  phone: string | null;
  booking_url: string | null;
  end_of_video_url: string;
  state?: string;
}

export interface AgentCard {
  agent_slug: string;
  label: string;
  agent_phone: string | null;
  agent_email: string | null;
  agent_license: string | null;
  agent_headshot_url: string | null;
  root_video_id: string | null;
}

export interface Preroll {
  videoId: string;
  playbackUrl: string;
  source: "agent" | "default";
}

export interface BranchingOption {
  label: string;
  branchVideoId: string;
  // null when this option is a "continue main video" sentinel.
  playbackUrl: string | null;
}

export interface BranchingPoint {
  timestamp: number;
  question: string;
  options: BranchingOption[];
}

export interface Branching {
  points: BranchingPoint[];
}

export interface IceServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}

export interface CallConfig {
  wss_url: string;
  app_name: string;
  lo_stream_id: string;
  consumer_stream_id: string;
  ice_servers: IceServer[];
}

export interface ConsumerLOPageResponse {
  lo: LOProfile;
  agents: AgentCard[];
  selected: AgentCard | null;
  preroll: Preroll | null;
  branching: Branching | null;
  flags: { video_call_enabled: boolean };
  call: CallConfig;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

const API_BASE = import.meta.env.VITE_API_BASE as string;

export async function fetchLOPage(
  webAppId: string,
  agentSlug?: string
): Promise<ConsumerLOPageResponse> {
  const url = new URL(`${API_BASE}/consumer/lo-page`);
  url.searchParams.set("web_app_id", webAppId);
  if (agentSlug) url.searchParams.set("agent_slug", agentSlug);

  const res = await fetch(url.toString(), {
    method: "GET",
    // Bypass the browser cache so background polling sees flag flips
    // (LO going live / offline) within the poll interval, not the
    // server-set Cache-Control window.
    cache: "no-store",
    headers: { Accept: "application/json" },
  });
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    /* fall through */
  }
  if (!res.ok) {
    const msg =
      (body && typeof body === "object" && "message" in body
        ? String((body as Record<string, unknown>).message)
        : null) || `HTTP ${res.status}`;
    throw new ApiError(res.status, msg);
  }
  return body as ConsumerLOPageResponse;
}
